import { Search } from '@moonpig/web-core-nav'
import React, { useCallback, useEffect, useState } from 'react'
import { useTrackGAEventOnce } from '@moonpig/web-core-analytics'
import {
  MAX_SUGGESTIONS,
  DEFAULT_TAB_NAME,
  MAX_ALL_SUGGESTIONS,
} from '@moonpig/web-core-nav/src/Search/constants'
import { Reminder } from '@moonpig/web-core-nav/src/Search/Reminders/Reminder'
import { SubmitItemType } from '@moonpig/web-core-nav/src/Search'
import { SearchInspirationType as Inspiration } from './searchInspirations/useSearchInspirations'
import { FlatNavigationalSuggestion } from './navigationalSuggestions'
import { SearchSuggestion } from './suggestions/Suggestions'
import { triggerGAEvent } from './analytics'
import {
  trackClearSearchEvent,
  trackRemindersViewedEvent,
  trackSuggestionsTabSelectedEvent,
  trackSuggestionViewEvent,
} from './tracking'
import {
  handleSubmitNavigationalSuggestion,
  handleSubmitReminderSuggestion,
  handleSubmitSearch,
  handleSubmitSearchFallback,
  handleSubmitSuggestion,
  handleSubmitInspirationSuggestion,
} from './handleSubmit'

type TrackedSearchProps = Overwrite<
  React.ComponentProps<typeof Search>,
  {
    navigationalSuggestions?: FlatNavigationalSuggestion[]
    items: SearchSuggestion[]
  }
> & {
  searchValue: string
  searchDepartment?: {
    name: string
    title: string
  }
  reminders?: Reminder[]
  inspirations?: Inspiration[]
}

export const TrackedSearch: React.FC<TrackedSearchProps> = ({
  searchDepartment,
  navigationalSuggestions,
  items,
  onSubmit,
  onKeyUp,
  onFocus,
  onClear,
  searchValue,
  reminders,
  inspirations,
  ...props
}) => {
  const [hadFocus, setHadFocus] = useState(false)
  const [hadInteraction, setHadInteraction] = useState(false)
  const [selectedTab, setSelectedTab] = useState(DEFAULT_TAB_NAME)
  const [suggestionsShowEventSent, setSuggestionsShowEventSent] =
    useState(false)
  const { trackGAEventOnce } = useTrackGAEventOnce()

  const calculateCount = () => {
    const isAllTab = selectedTab === DEFAULT_TAB_NAME
    const numberOfItems = isAllTab
      ? items.length
      : items.filter(
          item => item.department.title === selectedTab.toLowerCase(),
        ).length
    return Math.min(
      numberOfItems,
      isAllTab ? MAX_ALL_SUGGESTIONS : MAX_SUGGESTIONS,
    )
  }

  useEffect(() => {
    if (searchValue.length > 2 && items.length && !suggestionsShowEventSent) {
      trackSuggestionViewEvent({
        searchValue,
        count: calculateCount(),
        department: selectedTab,
      })
      setSuggestionsShowEventSent(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, items, searchDepartment.name, selectedTab])

  useEffect(() => {
    if (items.length) {
      if (searchValue.length > 2) {
        trackSuggestionViewEvent({
          searchValue,
          count: calculateCount(),
          department: selectedTab,
        })
      }
      trackSuggestionsTabSelectedEvent(selectedTab)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab])

  useEffect(() => {
    if (
      navigationalSuggestions &&
      navigationalSuggestions?.length > 0 &&
      !suggestionsShowEventSent
    ) {
      trackSuggestionViewEvent({
        department: searchDepartment.name,
        count: 1,
        searchValue,
      })
      setSuggestionsShowEventSent(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationalSuggestions, searchDepartment.name, searchValue])

  const trackOnFocus = useCallback(
    (value: string) => {
      if (!hadFocus) {
        setHadFocus(true)
        triggerGAEvent({
          event: 'ga_searchFocus',
        })
      }
      onFocus(value)
    },
    [hadFocus, onFocus],
  )

  const trackSubmit = useCallback(
    (submitItem: SubmitItemType) => {
      switch (submitItem.itemType) {
        case 'reminder':
          handleSubmitReminderSuggestion({
            reminders,
            submitItem,
            department: searchDepartment.name,
          })
          break

        case 'suggestion':
          handleSubmitSuggestion({
            items,
            submitItem,
            department: selectedTab,
          })
          break

        case 'navigational':
          handleSubmitNavigationalSuggestion({
            navigationalSuggestions,
            submitItem,
            department: searchDepartment.name,
          })
          break

        case 'search-fallback':
          handleSubmitSearchFallback({ submitItem, selectedTab })
          break

        case 'inspiration':
          handleSubmitInspirationSuggestion({
            inspirations,
            submitItem,
            department: searchDepartment.name,
          })
          break

        default:
          handleSubmitSearch(submitItem)
          break
      }
      onSubmit(submitItem)
    },
    [
      items,
      navigationalSuggestions,
      onSubmit,
      reminders,
      searchDepartment.name,
      selectedTab,
      inspirations,
    ],
  )

  const trackKeyUp = useCallback(
    (value: string) => {
      if (!hadInteraction && value.length === 1) {
        setHadInteraction(true)
        triggerGAEvent({
          event: 'ga_searchInput',
        })
      }

      onKeyUp(value)
    },
    [hadInteraction, onKeyUp],
  )

  const trackOnClear = useCallback(() => {
    trackClearSearchEvent()
    onClear()
    setSuggestionsShowEventSent(false)
  }, [onClear])

  const trackRemindersVisible = useCallback(() => {
    if (reminders) {
      trackRemindersViewedEvent({
        numberOfReminders: reminders.length,
        department: searchDepartment.name,
        trackGAEventOnce,
      })
    }
  }, [reminders, searchDepartment.name, trackGAEventOnce])

  return (
    <Search
      {...props}
      reminders={reminders}
      inspirations={inspirations}
      items={items}
      onSubmit={trackSubmit as (submitItem: SubmitItemType) => void}
      onTabChange={setSelectedTab}
      onKeyUp={trackKeyUp}
      onFocus={trackOnFocus}
      onClear={trackOnClear}
      onRemindersVisible={trackRemindersVisible}
      navigationalSuggestions={navigationalSuggestions}
      searchDepartment={searchDepartment}
    />
  )
}
